import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  assignConversation,
  failedMessageOf,
  getConversation,
  getInboxChanges,
  getThread,
  linkPatient as linkConversationPatient,
  listConversations,
  markConversationRead,
  retryMessage,
  sendMessage,
} from '../services/whatsappApi';
import type {
  Assignee,
  AssignmentEvent,
  ConversationDetail,
  Counters,
  InboxFilter,
  ThreadMessage,
} from '../types/whatsapp';
import { actionErrorText, clientIdFor, emptyDraft, newClientId, reconcileDraft } from './composer';
import type { ActionKind, Draft } from './composer';
import { compareConversations, mergeConversations } from './conversationList';
import type { ListView } from './conversationList';
import { fetchClinicTimeZone, DEFAULT_TIME_ZONE } from './clinicTimeZone';
import { handoffsToNotify, serverNowFromCursor } from './handoffs';
import { applyChanges, handoffNotificationBody, nextDelay, viewKey } from './inboxLogic';
import type { InboxData } from './inboxLogic';
import { lastMessageId, mergeThread, oldestCreatedAt } from './thread';
import { useWhatsApp } from './WhatsAppContext';

// One polling loop for the whole app (inbox/changes). It runs only
// when the clinic has a connected number, keeps the Navbar counter fresh on every
// page, raises the handoff notifications and, while the inbox page is mounted, keeps
// the list and the open conversation up to date. The plan says it is not paused when
// the tab is hidden: handoffs must be heard while staff work in another tab.

const PAGE_SIZE = 30;
const THREAD_PAGE_SIZE = 50;

export type PollStatus = 'idle' | 'ready' | 'offline' | 'stopped';
export type DetailState = 'idle' | 'loading' | 'ready' | 'notfound' | 'error';
export type NotificationPermissionState = NotificationPermission | 'unsupported';

interface InboxContextValue {
  status: PollStatus;
  counters: Counters | null;
  timeZone: string;

  view: ListView;
  setFilter: (filter: InboxFilter) => void;
  setUnanswered: (unanswered: boolean) => void;
  setQuery: (q: string) => void;
  list: InboxData['list'];
  total: number;
  hasMore: boolean;
  listLoading: boolean;
  listError: boolean;
  reloadList: () => void;
  loadMore: () => void;
  /** The inbox page mounts and unmounts; the list is only kept while it is mounted. */
  setPageActive: (active: boolean) => void;

  openId: number | null;
  /** `force` loads it again even when it is already the open one (retry after an error). */
  open: (id: number | null, force?: boolean) => void;
  detail: ConversationDetail | null;
  detailState: DetailState;
  thread: InboxData['thread'];
  threadHasMore: boolean;
  threadLoading: boolean;
  loadOlder: () => void;

  notifications: NotificationPermissionState;
  requestNotifications: () => void;

  /** Text of the composer of a conversation (kept while another one is open). */
  draftText: (conversationId: number) => string;
  setDraftText: (conversationId: number, text: string) => void;
  /** Conversations with a send running. */
  sendingIds: ReadonlySet<number>;
  /** Messages being retried. */
  retryingIds: ReadonlySet<number>;
  /** A take or return is running. */
  assigning: boolean;
  /** The last failure of an action on the open conversation, ready to show. */
  actionError: { conversationId: number; text: string } | null;
  send: (conversationId: number) => Promise<void>;
  retry: (conversationId: number, messageId: number) => Promise<void>;
  assign: (conversationId: number, to: Assignee) => Promise<void>;
  /** Polls right now (or right after the poll in flight): for changes the server made on its own. */
  pollNow: () => void;
  /** Links the conversation to a patient file; rejects with the request's error so the dialog can show it. */
  linkPatient: (conversationId: number, patientId: number) => Promise<void>;
}

const noop = () => {};
const InboxContext = createContext<InboxContextValue>({
  status: 'idle',
  counters: null,
  timeZone: DEFAULT_TIME_ZONE,
  view: { filter: 'all', unanswered: false, q: '' },
  setFilter: noop,
  setUnanswered: noop,
  setQuery: noop,
  list: [],
  total: 0,
  hasMore: false,
  listLoading: false,
  listError: false,
  reloadList: noop,
  loadMore: noop,
  setPageActive: noop,
  openId: null,
  open: noop,
  detail: null,
  detailState: 'idle',
  thread: [],
  threadHasMore: false,
  threadLoading: false,
  loadOlder: noop,
  notifications: 'unsupported',
  requestNotifications: noop,
  draftText: () => '',
  setDraftText: noop,
  sendingIds: new Set<number>(),
  retryingIds: new Set<number>(),
  assigning: false,
  actionError: null,
  send: async () => {},
  retry: async () => {},
  assign: async () => {},
  pollNow: noop,
  linkPatient: async () => {},
});

const EMPTY: InboxData = { list: [], hasMore: false, counters: null, thread: [] };
const INITIAL_VIEW: ListView = { filter: 'all', unanswered: false, q: '' };

const notificationPermission = (): NotificationPermissionState =>
  typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';

const isAuthFailure = (err: unknown): boolean => err instanceof ApiError && err.isAuthFailure;

const splitItems = (items: ReadonlyArray<ThreadMessage | AssignmentEvent>) => ({
  messages: items.filter((i): i is ThreadMessage => i.kind === 'message'),
  events: items.filter((i): i is AssignmentEvent => i.kind === 'event'),
});

export const InboxProvider = ({ children }: { children: ReactNode }) => {
  const { enabled, refreshAccount } = useWhatsApp();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [data, setData] = useState<InboxData>(EMPTY);
  const dataRef = useRef<InboxData>(EMPTY);
  const commit = useCallback((next: InboxData) => {
    dataRef.current = next;
    setData(next);
  }, []);

  const [status, setStatus] = useState<PollStatus>('idle');
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [view, setViewState] = useState<ListView>(INITIAL_VIEW);
  const viewRef = useRef<ListView>(INITIAL_VIEW);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailState, setDetailState] = useState<DetailState>('idle');
  const [threadHasMore, setThreadHasMore] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationPermissionState>(notificationPermission);

  const cursorRef = useRef<string | null>(null);
  const notBeforeRef = useRef(0);
  const seenRef = useRef<Set<number>>(new Set());
  const failuresRef = useRef(0);
  const stoppedRef = useRef(false);
  const inflightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageActiveRef = useRef(false);
  const openIdRef = useRef<number | null>(null);
  /** The conversation whose first thread page has arrived: only then does the poll ask for its messages. */
  const threadReadyRef = useRef<number | null>(null);
  const listSeqRef = useRef(0);
  const listBusyRef = useRef(false);
  const openSeqRef = useRef(0);
  const runRef = useRef<(() => Promise<void>) | null>(null);
  const pollAgainRef = useRef(false);
  const draftsRef = useRef<Map<number, Draft>>(new Map());
  const sendingRef = useRef<Set<number>>(new Set());
  const retryingRef = useRef<Set<number>>(new Set());
  const [draftTexts, setDraftTexts] = useState<Record<number, string>>({});
  const [sendingIds, setSendingIds] = useState<ReadonlySet<number>>(new Set());
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<number>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [actionError, setActionError] = useState<{ conversationId: number; text: string } | null>(null);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    setStatus('stopped');
  }, []);

  const takeCursor = useCallback((cursor: string) => {
    if (cursorRef.current) return;
    cursorRef.current = cursor;
    notBeforeRef.current = serverNowFromCursor(cursor);
  }, []);

  // ── List ────────────────────────────────────────────────────────────────────

  const loadList = useCallback(
    async (offset = 0) => {
      const v = viewRef.current;
      const seq = ++listSeqRef.current;
      listBusyRef.current = true;
      setListLoading(true);
      try {
        const res = await listConversations({
          filter: v.filter,
          unanswered: v.unanswered,
          q: v.q,
          limit: PAGE_SIZE,
          offset,
        });
        if (seq !== listSeqRef.current) return;
        const held = dataRef.current;
        let list = res.conversations;
        if (offset > 0) {
          const byId = new Map(held.list.map((c) => [c.id, c]));
          for (const c of res.conversations) byId.set(c.id, c);
          list = [...byId.values()].sort(compareConversations);
        }
        commit({
          ...held,
          list,
          hasMore: offset + res.conversations.length < res.total,
          counters: res.counters,
        });
        setTotal(res.total);
        setListError(false);
        takeCursor(res.cursor);
      } catch (err) {
        if (seq !== listSeqRef.current) return;
        if (isAuthFailure(err)) stop();
        else setListError(true);
      } finally {
        if (seq === listSeqRef.current) {
          listBusyRef.current = false;
          setListLoading(false);
        }
      }
    },
    [commit, stop, takeCursor],
  );

  const patchView = useCallback(
    (patch: Partial<ListView>) => {
      const next = { ...viewRef.current, ...patch };
      if (viewKey(next) === viewKey(viewRef.current)) return;
      viewRef.current = next;
      setViewState(next);
      if (pageActiveRef.current) void loadList(0);
    },
    [loadList],
  );

  const setFilter = useCallback((filter: InboxFilter) => patchView({ filter }), [patchView]);
  const setUnanswered = useCallback((unanswered: boolean) => patchView({ unanswered }), [patchView]);
  const setQuery = useCallback((q: string) => patchView({ q }), [patchView]);
  const reloadList = useCallback(() => void loadList(0), [loadList]);
  const loadMore = useCallback(() => {
    if (!dataRef.current.hasMore || listBusyRef.current) return;
    void loadList(dataRef.current.list.length);
  }, [loadList]);

  // ── Open conversation ───────────────────────────────────────────────────────

  const refreshDetail = useCallback(async () => {
    const id = openIdRef.current;
    if (id === null) return;
    try {
      const d = await getConversation(id);
      if (openIdRef.current === id) setDetail(d);
    } catch (err) {
      if (isAuthFailure(err)) stop();
    }
  }, [stop]);

  const open = useCallback(
    (id: number | null, force = false) => {
      if (openIdRef.current === id && !force) return;
      openIdRef.current = id;
      threadReadyRef.current = null;
      const seq = ++openSeqRef.current;
      setOpenId(id);
      setActionError(null);
      setDetail(null);
      setThreadHasMore(false);
      setThreadLoading(false);
      commit({ ...dataRef.current, thread: [] });
      if (id === null) {
        setDetailState('idle');
        return;
      }
      setDetailState('loading');
      setThreadLoading(true);
      Promise.all([getConversation(id), getThread(id, undefined, THREAD_PAGE_SIZE)])
        .then(([d, page]) => {
          if (seq !== openSeqRef.current) return;
          const { messages, events } = splitItems(page.items);
          commit({ ...dataRef.current, thread: mergeThread([], messages, events) });
          setDetail(d);
          setDetailState('ready');
          setThreadHasMore(page.has_more);
          threadReadyRef.current = id;
          // Opening a team conversation marks the patient's last message as read in Meta.
          if (d.conversation.assigned_to === 'human-agent') void markConversationRead(id).catch(() => {});
        })
        .catch((err: unknown) => {
          if (seq !== openSeqRef.current) return;
          if (isAuthFailure(err)) stop();
          setDetailState(err instanceof ApiError && err.status === 404 ? 'notfound' : 'error');
        })
        .finally(() => {
          if (seq === openSeqRef.current) setThreadLoading(false);
        });
    },
    [commit, stop],
  );

  const loadOlder = useCallback(() => {
    const id = openIdRef.current;
    if (id === null || threadReadyRef.current !== id) return;
    const before = oldestCreatedAt(dataRef.current.thread);
    if (!before) return;
    const seq = openSeqRef.current;
    setThreadLoading(true);
    getThread(id, before, THREAD_PAGE_SIZE)
      .then((page) => {
        if (seq !== openSeqRef.current) return;
        const { messages, events } = splitItems(page.items);
        commit({ ...dataRef.current, thread: mergeThread(dataRef.current.thread, messages, events) });
        setThreadHasMore(page.has_more);
      })
      .catch((err: unknown) => {
        if (isAuthFailure(err)) stop();
      })
      .finally(() => {
        if (seq === openSeqRef.current) setThreadLoading(false);
      });
  }, [commit, stop]);

  const setPageActive = useCallback(
    (active: boolean) => {
      if (pageActiveRef.current === active) return;
      pageActiveRef.current = active;
      if (active) void loadList(0);
      else {
        listSeqRef.current++;
        listBusyRef.current = false;
        setListLoading(false);
        open(null);
        commit({ ...dataRef.current, list: [], hasMore: false });
      }
    },
    [commit, loadList, open],
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  const pollNow = useCallback(() => {
    if (stoppedRef.current) return;
    if (inflightRef.current) pollAgainRef.current = true;
    else void runRef.current?.();
  }, []);

  const mergeIntoThread = useCallback(
    (conversationId: number, messages: ThreadMessage[], events: AssignmentEvent[] = []) => {
      if (openIdRef.current !== conversationId) return;
      commit({ ...dataRef.current, thread: mergeThread(dataRef.current.thread, messages, events) });
    },
    [commit],
  );

  const getDraft = (conversationId: number): Draft => draftsRef.current.get(conversationId) ?? emptyDraft();
  const putDraft = useCallback((conversationId: number, draft: Draft) => {
    if (draft.text === '' && draft.pendingClientId === null) draftsRef.current.delete(conversationId);
    else draftsRef.current.set(conversationId, draft);
    setDraftTexts((prev) => ({ ...prev, [conversationId]: draft.text }));
  }, []);

  const setDraftText = useCallback(
    (conversationId: number, text: string) => putDraft(conversationId, { ...getDraft(conversationId), text }),
    [putDraft],
  );
  const draftText = useCallback((conversationId: number) => draftTexts[conversationId] ?? '', [draftTexts]);

  const markSet = (ref: { current: Set<number> }, set: (s: ReadonlySet<number>) => void, id: number, on: boolean) => {
    if (on) ref.current.add(id);
    else ref.current.delete(id);
    set(new Set(ref.current));
  };

  const failureOf = useCallback(
    (conversationId: number, err: unknown, kind: ActionKind) => {
      if (isAuthFailure(err)) {
        stop();
        return;
      }
      const code = err instanceof ApiError ? err.code : '';
      const fallback = err instanceof Error && err.message ? err.message : 'No se pudo completar la acción.';
      const text = actionErrorText(code, fallback, kind);
      if (text) setActionError({ conversationId, text });
      if (code === 'window_closed' || code === 'account_not_connected' || code === 'already_assigned') {
        void refreshDetail();
      }
      // The account may have just turned to `error` (an expired token): the screen must follow.
      if (code === 'account_not_connected') void refreshAccount();
    },
    [refreshAccount, refreshDetail, stop],
  );

  const send = useCallback(
    async (conversationId: number) => {
      if (sendingRef.current.has(conversationId)) return;
      const draft = getDraft(conversationId);
      const text = draft.text.trim();
      if (!text) return;
      markSet(sendingRef, setSendingIds, conversationId, true);
      setActionError(null);
      try {
        const clientId = clientIdFor(draft, text, newClientId);
        putDraft(conversationId, { ...draft, pendingClientId: clientId, pendingText: text });
        const res = await sendMessage(conversationId, text, clientId);
        // Stored (any status, including `failed`): the draft is done; a failed one is retried from the thread.
        putDraft(conversationId, emptyDraft());
        mergeIntoThread(conversationId, [res.message]);
      } catch (err) {
        const failed = failedMessageOf(err);
        if (failed) {
          putDraft(conversationId, emptyDraft());
          mergeIntoThread(conversationId, [failed]);
          void refreshAccount();
        } else {
          failureOf(conversationId, err, 'send');
        }
      } finally {
        markSet(sendingRef, setSendingIds, conversationId, false);
        pollNow();
      }
    },
    [failureOf, mergeIntoThread, pollNow, putDraft, refreshAccount],
  );

  const retry = useCallback(
    async (conversationId: number, messageId: number) => {
      if (retryingRef.current.has(messageId)) return;
      markSet(retryingRef, setRetryingIds, messageId, true);
      setActionError(null);
      try {
        mergeIntoThread(conversationId, [await retryMessage(conversationId, messageId)]);
      } catch (err) {
        const failed = failedMessageOf(err);
        if (failed) {
          mergeIntoThread(conversationId, [failed]);
          void refreshAccount();
        } else failureOf(conversationId, err, 'retry');
      } finally {
        markSet(retryingRef, setRetryingIds, messageId, false);
        pollNow();
      }
    },
    [failureOf, mergeIntoThread, pollNow, refreshAccount],
  );

  const assign = useCallback(
    async (conversationId: number, to: Assignee) => {
      if (assigning) return;
      setAssigning(true);
      setActionError(null);
      try {
        const res = await assignConversation(conversationId, to);
        mergeIntoThread(conversationId, [], [res.event]);
        // The user is now looking at a team conversation: same as opening it.
        if (to === 'human-agent') void markConversationRead(conversationId).catch(() => {});
      } catch (err) {
        failureOf(conversationId, err, 'assign');
      } finally {
        setAssigning(false);
        void refreshDetail();
        pollNow();
      }
    },
    [assigning, failureOf, mergeIntoThread, pollNow, refreshDetail],
  );

  const linkPatient = useCallback(
    async (conversationId: number, patientId: number) => {
      const res = await linkConversationPatient(conversationId, patientId);
      // The server answers with the new detail: show it now instead of waiting for the poll.
      if (openIdRef.current === conversationId) setDetail(res);
      const merged = mergeConversations(
        dataRef.current.list,
        [res.conversation],
        viewRef.current,
        dataRef.current.hasMore,
      );
      if (!merged.needsRefetch) commit({ ...dataRef.current, list: merged.items });
      pollNow();
    },
    [commit, pollNow],
  );

  // ── Notifications ───────────────────────────────────────────────────────────

  const requestNotifications = useCallback(() => {
    if (!('Notification' in window)) return;
    void Notification.requestPermission().then((p) => setNotifications(p));
  }, []);

  const notify = useCallback((resp: Awaited<ReturnType<typeof getInboxChanges>>) => {
    const fresh = handoffsToNotify(resp.new_handoffs, seenRef.current, notBeforeRef.current);
    for (const h of resp.new_handoffs) seenRef.current.add(h.event_id);
    if (fresh.length === 0 || notificationPermission() !== 'granted') return;
    for (const h of fresh) {
      try {
        const n = new Notification(h.title, {
          body: handoffNotificationBody(h.reason, h.urgent),
          tag: `handoff-${h.event_id}`,
        });
        n.onclick = () => {
          window.focus();
          navigateRef.current(`/whatsapp?c=${h.conversation_id}`);
          n.close();
        };
      } catch {
        // Some browsers refuse `new Notification` (mobile); the counter still updates.
      }
    }
  }, []);

  // ── Polling ─────────────────────────────────────────────────────────────────

  const tick = useCallback(async () => {
    if (inflightRef.current || stoppedRef.current) return;
    inflightRef.current = true;
    try {
      if (!cursorRef.current) {
        const res = await listConversations({ limit: 1 });
        takeCursor(res.cursor);
        commit({ ...dataRef.current, counters: res.counters });
      } else {
        const active = pageActiveRef.current;
        const sent = { viewKey: viewKey(viewRef.current), openId: active ? openIdRef.current : null };
        const threadReady = sent.openId !== null && threadReadyRef.current === sent.openId;
        const resp = await getInboxChanges(
          cursorRef.current,
          threadReady
            ? { conversationId: sent.openId as number, afterMessageId: lastMessageId(dataRef.current.thread) }
            : undefined,
        );
        cursorRef.current = resp.cursor;
        notify(resp);
        // A message that arrived with the id of a draft means that send went through: drop the draft.
        const openNow = openIdRef.current;
        const held = openNow !== null ? draftsRef.current.get(openNow) : undefined;
        if (openNow !== null && held && resp.messages.length > 0) {
          const next = reconcileDraft(held, resp.messages);
          if (next !== held) putDraft(openNow, next);
        }

        const current = {
          viewKey: viewKey(viewRef.current),
          openId: pageActiveRef.current ? openIdRef.current : null,
        };
        if (!active || listBusyRef.current) {
          // No list on screen, or its fetch is running and will carry these changes (the 30 s overlap covers the rest).
          commit({ ...dataRef.current, counters: resp.counters });
        } else {
          const result = applyChanges(
            dataRef.current,
            resp,
            { ...sent, openId: threadReady ? sent.openId : null },
            current,
            viewRef.current,
          );
          commit(result.data);
          if (result.refetchList) void loadList(0);
          if (result.refreshDetail) void refreshDetail();
        }
      }
      failuresRef.current = 0;
      setStatus('ready');
    } catch (err) {
      if (isAuthFailure(err)) stop();
      else {
        failuresRef.current += 1;
        setStatus('offline');
      }
    } finally {
      inflightRef.current = false;
    }
  }, [commit, loadList, notify, putDraft, refreshDetail, stop, takeCursor]);

  useEffect(() => {
    if (!enabled) {
      cursorRef.current = null;
      stoppedRef.current = false;
      failuresRef.current = 0;
      seenRef.current.clear();
      pageActiveRef.current = false;
      commit(EMPTY);
      setStatus('idle');
      return;
    }

    stoppedRef.current = false;
    let alive = true;
    const schedule = (ms: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(run, ms);
    };
    const run = async () => {
      await tick();
      // An action asked for a poll while one was running: go again right away.
      if (pollAgainRef.current) {
        pollAgainRef.current = false;
        await tick();
      }
      if (alive && !stoppedRef.current) schedule(nextDelay(failuresRef.current));
    };
    runRef.current = run;

    void fetchClinicTimeZone().then((tz) => {
      if (alive) setTimeZone(tz);
    });
    void run();

    // Hidden tabs are throttled by the browser; poll as soon as the tab is visible again.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void run();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      runRef.current = null;
      document.removeEventListener('visibilitychange', onVisible);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, tick, commit]);

  const value = useMemo<InboxContextValue>(
    () => ({
      status,
      counters: data.counters,
      timeZone,
      view,
      setFilter,
      setUnanswered,
      setQuery,
      list: data.list,
      total,
      hasMore: data.hasMore,
      listLoading,
      listError,
      reloadList,
      loadMore,
      setPageActive,
      openId,
      open,
      detail,
      detailState,
      thread: data.thread,
      threadHasMore,
      threadLoading,
      loadOlder,
      notifications,
      requestNotifications,
      draftText,
      setDraftText,
      sendingIds,
      retryingIds,
      assigning,
      actionError,
      send,
      retry,
      assign,
      pollNow,
      linkPatient,
    }),
    [
      status,
      data,
      timeZone,
      view,
      setFilter,
      setUnanswered,
      setQuery,
      total,
      listLoading,
      listError,
      reloadList,
      loadMore,
      setPageActive,
      openId,
      open,
      detail,
      detailState,
      threadHasMore,
      threadLoading,
      loadOlder,
      notifications,
      requestNotifications,
      draftText,
      setDraftText,
      sendingIds,
      retryingIds,
      assigning,
      actionError,
      send,
      retry,
      assign,
      pollNow,
      linkPatient,
    ],
  );

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};

export const useInbox = (): InboxContextValue => useContext(InboxContext);
